import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/lib/anim";

export const Container = ({ children, className = "" }) => (
  <div className={`mx-auto w-full max-w-[1200px] px-5 sm:px-8 ${className}`}>{children}</div>
);

export const Eyebrow = ({ children, dark = false }) => (
  <span
    className={`inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.18em] ${
      dark ? "text-brand-gradient-light" : "text-brand-gradient"
    }`}
  >
    <span className={`h-px w-6 ${dark ? "bg-[#5EEAD4]/60" : "bg-[#2563EB]/50"}`} aria-hidden="true" />
    {children}
  </span>
);

export function SectionHead({ eyebrow, title, copy, dark = false, center = false, className = "" }) {
  return (
    <Reveal className={`max-w-3xl ${center ? "mx-auto text-center" : ""} ${className}`}>
      {eyebrow && (
        <div className={center ? "flex justify-center" : ""}>
          <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
        </div>
      )}
      <h2
        className={`mt-4 text-[32px] font-semibold leading-[1.12] tracking-tight sm:text-4xl lg:text-[44px] ${
          dark ? "text-white" : "text-[#1D305B]"
        }`}
      >
        {title}
      </h2>
      {copy && (
        <p className={`mt-5 text-base leading-relaxed sm:text-lg ${dark ? "text-[#94A3B8]" : "text-[#64748B]"}`}>
          {copy}
        </p>
      )}
    </Reveal>
  );
}

export function PrimaryButton({ to = "/contact", children, dark = false, className = "", testid }) {
  return (
    <Link
      data-testid={testid}
      to={to}
      className={`group inline-flex items-center gap-2.5 whitespace-nowrap rounded-[10px] px-6 py-3.5 text-[15px] font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
        dark
          ? "bg-white text-[#1D305B] hover:shadow-[0_16px_40px_-12px_rgba(255,255,255,0.35)]"
          : "btn-brand text-white hover:shadow-[0_16px_36px_-12px_rgba(37,99,235,0.55)]"
      } ${className}`}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
    </Link>
  );
}

export function SecondaryButton({ to = "/platforms", children, dark = false, className = "", testid }) {
  return (
    <Link
      data-testid={testid}
      to={to}
      className={`group inline-flex items-center gap-2.5 whitespace-nowrap rounded-[10px] border px-6 py-3.5 text-[15px] font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
        dark
          ? "border-white/25 text-white hover:border-white/60 hover:bg-white/5"
          : "border-[#CBD5E1] bg-white text-[#1D305B] hover:border-[#1D305B]/40 hover:shadow-[0_12px_28px_-14px_rgba(15,23,42,0.35)]"
      } ${className}`}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
    </Link>
  );
}

export function PageHero({ eyebrow, title, copy, children, testid }) {
  return (
    <section className="relative overflow-hidden border-b border-[#E2E8F0] bg-[#F8FAFC]">
      <div
        className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_75%_65%_at_50%_0%,black,transparent)]"
        aria-hidden="true"
      />
      <Container className="relative pb-16 pt-14 sm:pb-20 sm:pt-16 lg:pb-24 lg:pt-20">
        <Reveal className="max-w-3xl">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h1
            data-testid={testid}
            className="mt-4 text-4xl font-semibold leading-[1.06] tracking-tight text-[#1D305B] sm:text-5xl lg:text-[56px]"
          >
            {title}
          </h1>
          {copy && <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#64748B] sm:text-lg">{copy}</p>}
        </Reveal>
        {children}
      </Container>
    </section>
  );
}

export function FeatureGrid({ items, cols = 3, dark = false, testidPrefix = "feature" }) {
  const colClass = cols === 4 ? "lg:grid-cols-4" : cols === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3";
  return (
    <div className={`grid gap-5 sm:grid-cols-2 ${colClass}`}>
      {items.map((f, i) => (
        <Reveal
          key={f.title}
          delay={(i % cols) * 70}
          data-testid={`${testidPrefix}-${i}`}
          className={`group rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1 ${
            dark
              ? "border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.07]"
              : "border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_18px_40px_-18px_rgba(15,23,42,0.18)]"
          }`}
        >
          <div
            className={`flex h-11 w-11 items-center justify-center rounded-[10px] border ${
              dark ? "border-white/10 bg-white/[0.06] text-[#5EEAD4]" : "border-[#E2E8F0] bg-[#F8FAFC] text-[#2563EB]"
            }`}
          >
            {f.icon}
          </div>
          <h3 className={`mt-5 text-lg font-semibold ${dark ? "text-white" : "text-[#1D305B]"}`}>{f.title}</h3>
          <p className={`mt-2.5 text-[15px] leading-relaxed ${dark ? "text-[#94A3B8]" : "text-[#64748B]"}`}>
            {f.desc}
          </p>
        </Reveal>
      ))}
    </div>
  );
}

export function CheckList({ items, dark = false, twoCol = false }) {
  return (
    <ul className={`grid gap-3 ${twoCol ? "sm:grid-cols-2" : ""}`}>
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
              dark ? "bg-[#0EA5A4]/20 text-[#5EEAD4]" : "bg-[#2563EB]/10 text-[#2563EB]"
            }`}
          >
            <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
          </span>
          <span className={`text-[15px] leading-relaxed ${dark ? "text-[#CBD5E1]" : "text-[#475569]"}`}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function SplitSection({ eyebrow, title, copy, points = [], visual, reverse = false, dark = false, cta = null }) {
  return (
    <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
      <Reveal className={reverse ? "lg:order-2" : ""}>
        <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
        <h2
          className={`mt-4 text-[30px] font-semibold leading-[1.15] tracking-tight sm:text-4xl ${
            dark ? "text-white" : "text-[#1D305B]"
          }`}
        >
          {title}
        </h2>
        {copy && (
          <p className={`mt-5 text-base leading-relaxed sm:text-lg ${dark ? "text-[#94A3B8]" : "text-[#64748B]"}`}>
            {copy}
          </p>
        )}
        {points.length > 0 && (
          <div className="mt-7">
            <CheckList items={points} dark={dark} twoCol={points.length > 5} />
          </div>
        )}
        {cta && <div className="mt-8">{cta}</div>}
      </Reveal>
      <Reveal delay={140} className={reverse ? "lg:order-1" : ""}>
        {visual}
      </Reveal>
    </div>
  );
}

export function CTASection({
  title = (
    <>
      Building the next generation of <span className="text-brand-gradient-light">financial products?</span>
    </>
  ),
  copy = "Let's talk about the infrastructure, integrations and technology required to make it happen.",
  primary = { to: "/contact", label: "Talk to iJeeva", testid: "cta-talk-to-ijeeva" },
  secondary = { to: "/platforms", label: "Explore Our Platforms", testid: "cta-explore-platforms" },
}) {
  return (
    <section data-testid="final-cta-section" className="relative overflow-hidden bg-[#1D305B]">
      <div className="bg-grid-dark absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_60%_80%_at_50%_50%,black,transparent)]" aria-hidden="true" />
      <svg className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.16]" width="900" height="900" viewBox="0 0 900 900" fill="none" aria-hidden="true">
        <circle cx="450" cy="450" r="180" stroke="#2563EB" strokeWidth="1" strokeDasharray="3 9" className="flow-dash-slow" />
        <circle cx="450" cy="450" r="300" stroke="#0EA5A4" strokeWidth="1" strokeDasharray="3 12" className="flow-dash-slow" />
        <circle cx="450" cy="450" r="430" stroke="#2563EB" strokeWidth="0.75" strokeDasharray="2 14" className="flow-dash-slow" />
      </svg>
      <Container className="relative py-20 text-center lg:py-28">
        <Reveal>
          <h2 className="mx-auto max-w-3xl text-[32px] font-semibold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-[48px]">
            {title}
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-[#94A3B8] sm:text-lg">{copy}</p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <PrimaryButton dark to={primary.to} testid={primary.testid}>
              {primary.label}
            </PrimaryButton>
            <SecondaryButton dark to={secondary.to} testid={secondary.testid}>
              {secondary.label}
            </SecondaryButton>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
